// ASC-22
import { MonthYearPeriod, spaceToDash } from '@common/neo.tax';
import { Alert, BadgeColorVariant, Button, Loading, ModalActions, Stack, Typography } from '@ui-components/neo.tax';

import { AllocationSlider } from '../allocation-slider';
import {
  Divider,
  StyledModal,
  StyledSlidersStack,
  StyledStack,
  StyledKeywordSearch,
  StyledLeftColumn,
  StyledRightColumn,
  StyledHeaderPlaceholder,
} from './allocation-modal.styled';
import { PerProjectAllocation } from './allocation-modal.types';
import { AllocationModalHeader } from './AllocationModalHeader';

export type AllocationModalProps<T extends PerProjectAllocation> = {
  period: MonthYearPeriod;
  isOpen: boolean;
  close: () => void;
  selectedProjectName?: string;
  selectedProjectAllocation?: T;
  nonProjectPercent: number;
  updateNonProjectPercent: (percent: number) => void;
  expenseName: string;
  hasUnsavedChanges: boolean;
  error?: string | null;
  projectAllocations: T[] | null;
  projectAllocationsLoading: boolean;
  updateProjectAllocation: ({ allocationPercent, id }: { id: string; allocationPercent: number }) => void;
  isForeignExpense?: boolean;
  loadingUpdateAllocations: boolean;
  submit: () => void;
  nonProjectAllocationTitle: string;
  nonProjectAllocationHelpText?: string;
  endOfListWarningText?: string;
  getBadgeConfig?: (projectAllocation: T) => { colorVariant: BadgeColorVariant; label: string };
  searchKeyword?: string | null;
  setSearchKeyword?: React.Dispatch<React.SetStateAction<string | null>>;
  SummaryComponent?: React.ReactElement;
};

/**
 * Note that assigned and unassigned only refers to the initial status of the projects. When there are unsaved changes,
 * unassigned projects with new allocations should continue to be passed in as unassigned in order to maintain ordering.
 */
export const AllocationModal = <T extends PerProjectAllocation>({
  SummaryComponent,
  close,
  endOfListWarningText,
  error,
  expenseName,
  getBadgeConfig,
  hasUnsavedChanges,
  isForeignExpense,
  isOpen,
  loadingUpdateAllocations,
  nonProjectAllocationHelpText,
  nonProjectAllocationTitle,
  nonProjectPercent,
  period,
  projectAllocations,
  projectAllocationsLoading,
  searchKeyword,
  selectedProjectAllocation,
  selectedProjectName,
  setSearchKeyword,
  submit,
  updateNonProjectPercent,
  updateProjectAllocation,
}: AllocationModalProps<T>): React.ReactElement | null => (
  <StyledModal isOpen={isOpen} setIsOpen={close}>
    <Stack>
      <Stack direction="column" paddingX={6} paddingY={6}>
        <AllocationModalHeader
          expenseName={expenseName}
          period={period}
          selectedProjectName={selectedProjectName}
          isForeignExpense={isForeignExpense}
        />

        <StyledSlidersStack direction="column" gap={6} fullWidth>
          <StyledSlidersStack fullWidth>
            <StyledLeftColumn direction="column">
              {searchKeyword !== undefined && setSearchKeyword && <StyledHeaderPlaceholder />}
              {/* Non-project allocation */}
              <StyledStack gap={6} paddingY={6} paddingX={3} direction="column" fullWidth>
                <Stack direction="column" gap={1}>
                  <AllocationSlider
                    allocationPercent={nonProjectPercent}
                    label={nonProjectAllocationTitle}
                    onChange={updateNonProjectPercent}
                  />
                  {nonProjectAllocationHelpText && (
                    <Typography fontSize="xs">* {nonProjectAllocationHelpText}</Typography>
                  )}
                </Stack>
              </StyledStack>
            </StyledLeftColumn>
            <Divider />

            {/* Per-project allocation */}
            <StyledRightColumn direction="column" fullWidth>
              {searchKeyword !== undefined && setSearchKeyword && (
                <StyledHeaderPlaceholder paddingX={3} paddingY={3}>
                  <StyledKeywordSearch searchKeyword={searchKeyword} setSearchKeyword={setSearchKeyword} />
                </StyledHeaderPlaceholder>
              )}
              <StyledStack as="ul" gap={6} paddingY={6} paddingX={3} direction="column" fullWidth>
                {projectAllocationsLoading && (
                  <Stack as="li" direction="column">
                    <Loading height={120} />
                  </Stack>
                )}
                {selectedProjectAllocation && (
                  <Stack as="li" direction="column">
                    <AllocationSlider
                      id={selectedProjectAllocation.projectId}
                      analyticId={`${spaceToDash(selectedProjectAllocation.projectName)}-allocation-slider`}
                      allocationPercent={selectedProjectAllocation.allocationPercent}
                      label={selectedProjectAllocation.projectName}
                      index={0}
                      onChange={updateProjectAllocation}
                      badge={getBadgeConfig ? getBadgeConfig(selectedProjectAllocation) : undefined}
                    />
                  </Stack>
                )}
                {!projectAllocationsLoading &&
                  projectAllocations &&
                  projectAllocations.map((projectAllocation, index) => {
                    const { allocationPercent, projectId, projectName } = projectAllocation;
                    const sliderIndex = selectedProjectAllocation ? index + 1 : index;
                    return (
                      <Stack as="li" direction="column" key={projectId}>
                        <AllocationSlider
                          id={projectId}
                          analyticId={`${spaceToDash(projectName)}-allocation-slider`}
                          allocationPercent={allocationPercent}
                          label={projectName}
                          index={sliderIndex}
                          onChange={updateProjectAllocation}
                          badge={getBadgeConfig ? getBadgeConfig(projectAllocation) : undefined}
                        />
                      </Stack>
                    );
                  })}
                {endOfListWarningText && !searchKeyword && (
                  <Stack as="li" direction="column">
                    <Alert title={endOfListWarningText} severity="neutral" />
                  </Stack>
                )}
              </StyledStack>
            </StyledRightColumn>
          </StyledSlidersStack>
          {hasUnsavedChanges && (
            <Alert title={error ?? 'Ready to apply adjustments'} severity={error ? 'warning' : 'success'} />
          )}
        </StyledSlidersStack>

        <ModalActions>
          <Button variant="white" rounded="regular" size="base" onClick={close}>
            Cancel
          </Button>
          <Button
            variant="primary"
            rounded="regular"
            size="base"
            type="submit"
            disabled={!!error || !hasUnsavedChanges || loadingUpdateAllocations}
            onClick={submit}
          >
            Apply Adjustments
          </Button>
        </ModalActions>
      </Stack>
      {SummaryComponent}
    </Stack>
  </StyledModal>
);
